/*
 * SimCard — simulated payment card with 3D flip (front ↔ back).
 *
 * Front: PAN, holder, expiry, EMV chip, scheme badge.
 * Back: magnetic stripe, CVV, card ID.
 * Click or tap to flip. Mouse-move tilt on hover.
 */
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

const EmvChip = () => (
  <svg width="42" height="32" viewBox="0 0 42 32" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="ls-chip-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#E8C89A" />
        <stop offset="45%" stopColor="#C9A227" />
        <stop offset="100%" stopColor="#8C6D1F" />
      </linearGradient>
    </defs>
    <rect x="0.5" y="0.5" width="41" height="31" rx="5" fill="url(#ls-chip-grad)" stroke="rgba(0,0,0,0.35)" />
    <path d="M0 11h13M0 21h13M42 11H29M42 21H29M13 0v32M29 0v32M13 16h16" stroke="rgba(0,0,0,0.28)" strokeWidth="1.2" />
  </svg>
);

const CARD_STYLE = {
  aspectRatio: '1.586',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #1C2228 0%, #13171C 45%, #0A0D10 100%)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 18px 40px -22px rgba(0,0,0,0.9)',
  color: '#FFFFFF',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  position: 'absolute',
  top: 0, left: 0, width: '100%', height: '100%',
};

const SimCard = ({ card, holder, label = 'Simulated card', compact = false, selected = false, onClick }) => {
  const ref = useRef(null);
  const [flipped, setFlipped] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 14, ry: px * 14 });
  };

  const groupedPan = (card?.pan || '0000000000000000').replace(/(.{4})/g, '$1 ').trim();
  const pad = compact ? 14 : 20;

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(card);
  };

  const handleFlip = (e) => {
    e.stopPropagation();
    setFlipped(f => !f);
  };

  return (
    <div
      style={{ perspective: 900, cursor: 'pointer' }}
      onClick={handleClick}
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
        animate={{ rotateX: tilt.rx, rotateY: flipped ? 180 + tilt.ry : tilt.ry }}
        transition={{ type: 'spring', stiffness: 220, damping: 18 }}
        className="ls-noise relative w-full"
        data-testid="ls-sim-card"
        style={{
          aspectRatio: '1.586',
          transformStyle: 'preserve-3d',
          outline: selected ? '2px solid var(--ls-primary)' : 'none',
          outlineOffset: 3,
          borderRadius: 12,
        }}
      >
        {/* ── FRONT ── */}
        <div className="ls-noise overflow-hidden" style={{ ...CARD_STYLE, padding: pad }}>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute', top: -70, right: -50, width: 220, height: 220,
              background: 'radial-gradient(circle, rgba(255,94,0,0.30) 0%, transparent 62%)',
              pointerEvents: 'none',
            }}
          />
          <div className="relative flex flex-col h-full justify-between">
            <div className="flex items-start justify-between">
              <span className="ls-overline" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9 }}>
                {label}
              </span>
              <span className="ls-mono font-bold" style={{ color: 'var(--ls-primary)', fontSize: compact ? 10 : 12, letterSpacing: '0.14em' }}>
                {card?.scheme || 'SIMCARD'}
              </span>
            </div>
            <div className={compact ? 'mt-1' : 'mt-2'}><EmvChip /></div>
            <p className="ls-mono" data-testid="ls-sim-card-pan" style={{
              fontSize: compact ? 13 : 17, fontWeight: 500,
              letterSpacing: '0.06em', textShadow: '1px 1px 0px rgba(0,0,0,0.55)',
            }}>
              {groupedPan}
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="ls-overline" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 8 }}>Holder</p>
                <p className="ls-mono" style={{ fontSize: compact ? 10 : 12, letterSpacing: '0.08em' }}>
                  {holder || card?.holder || '—'}
                </p>
              </div>
              <div className="flex items-end gap-3">
                <button
                  onClick={handleFlip}
                  title="Flip card"
                  style={{
                    background: 'rgba(255,255,255,0.10)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 4,
                    padding: '3px 6px',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: 9,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 16V4l-4 4M17 8v12l4-4" />
                  </svg>
                  CVV
                </button>
                <div className="text-right">
                  <p className="ls-overline" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 8 }}>Expires</p>
                  <p className="ls-mono" style={{ fontSize: compact ? 10 : 12 }}>{card?.expiry || '--/--'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BACK ── */}
        <div
          className="ls-noise overflow-hidden"
          style={{
            ...CARD_STYLE,
            padding: 0,
            transform: 'rotateY(180deg)',
          }}
        >
          {/* Magnetic stripe */}
          <div style={{
            marginTop: compact ? 16 : 24,
            height: compact ? 32 : 42,
            background: 'linear-gradient(180deg, #1a1a1a 0%, #333 20%, #1a1a1a 40%, #2a2a2a 60%, #1a1a1a 80%, #333 100%)',
            width: '100%',
          }} />

          {/* Signature strip + CVV */}
          <div style={{ padding: `${compact ? 10 : 16}px ${pad}px` }}>
            <div className="flex items-center gap-3">
              <div className="flex-1" style={{
                height: compact ? 24 : 32,
                background: 'linear-gradient(90deg, #e8e4d8, #f5f1e6, #e8e4d8)',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)',
                }} />
              </div>
              <div className="text-center" style={{
                background: '#fff',
                borderRadius: 3,
                padding: compact ? '4px 10px' : '6px 14px',
                minWidth: compact ? 44 : 56,
              }}>
                <p className="ls-overline" style={{ color: '#666', fontSize: 7, marginBottom: 1 }}>CVV</p>
                <p className="ls-mono font-bold" style={{ color: '#000', fontSize: compact ? 14 : 18, letterSpacing: '0.12em' }}>
                  {card?.cvv || '---'}
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-end justify-between">
              <div>
                <p className="ls-overline" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 7 }}>Card ID</p>
                <p className="ls-mono" style={{ color: 'rgba(255,255,255,0.50)', fontSize: 9 }}>
                  {card?.card_id || card?.pan_masked || '—'}
                </p>
              </div>
              <button
                onClick={handleFlip}
                title="Flip back"
                style={{
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 4,
                  padding: '3px 6px',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 9,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 16V4l-4 4M17 8v12l4-4" />
                </svg>
                Front
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SimCard;
